import { addDays, differenceInMinutes, format, isValid, parse, subDays } from 'date-fns'
import { DateRange, NonEmptyDateRange } from '@mui/x-date-pickers-pro-7'
import { DAY_MS } from './constants'
import { localeDateFormat } from './localeUtils'

export const getUTCDateTime = (date: Date) => {
  const userTimezoneOffset = date.getTimezoneOffset() * 60000
  return new Date(date.getTime() - userTimezoneOffset)
}

// This function can be replaced by formatDateWithTimeZoneStr
export const getLocalDateString = (date: string | Date) =>
  new Date(date).toLocaleDateString(navigator.language)

export const getDateString = (date: string | Date) => {
  const newDate = new Date('2013-08-03T02:00:00Z')
  const year = newDate.getFullYear()
  const month = newDate.getMonth() + 1
  const dt = newDate.getDate()

  return `${dt < 10 ? '0' : ''}${dt}${month < 10 ? '0' : ''}${month}${year}`
}

export const getDateDiff = (from: Date, to: Date, span: 'day' | 'week'): number => {
  const msDiff =
    new Date(to.toDateString()).getTime() -
    new Date(`${from.toDateString()} 23:59:59.999`).getTime()
  switch (span) {
    case 'day':
      return Math.floor(msDiff / DAY_MS)
    case 'week':
      return Math.floor(msDiff / DAY_MS / 5)
    default:
      return msDiff
  }
}

export const getDateTime = (date: Date, includeSec = false, dateTimeSeperator = ' ') => {
  const dateSplit = format(date, 'yyyy-MM-dd hh-mm-ss').split(/[\sT:.-]+/)
  const returnDate = `${dateSplit[2]}/${dateSplit[1].padStart(2, '0')}/${dateSplit[0].padStart(
    2,
    '0'
  )}`
  let returnTime = `${dateSplit[3].padStart(2, '0')}:${dateSplit[4].padStart(2, '0')}`
  if (includeSec) {
    returnTime = `${returnTime}:${dateSplit[5].padStart(2, '0')}`
  }
  return `${returnDate}${dateTimeSeperator}${returnTime}`
}

export const getDateTime24HrFormat = (date: Date, includeSec = false, dateTimeSeperator = ' ') => {
  const dateSplit = format(date, 'yyyy-MM-dd HH-mm-ss').split(/[\sT:.-]+/)
  const returnDate = `${dateSplit[2]}/${dateSplit[1].padStart(2, '0')}/${dateSplit[0].padStart(
    2,
    '0'
  )}`
  let returnTime = `${dateSplit[3].padStart(2, '0')}:${dateSplit[4].padStart(2, '0')}`
  if (includeSec) {
    returnTime = `${returnTime}:${dateSplit[5].padStart(2, '0')}`
  }
  return `${returnDate}${dateTimeSeperator}${returnTime}`
}

export const getDateArrayFromRange = (
  startDate: Date,
  endDate: Date,
  formatString: string
): string[] => {
  const retVal = [format(startDate, formatString)]
  let current = startDate

  while (current < endDate) {
    current = addDays(current, 1)
    retVal.push(format(current, formatString))
  }

  return retVal
}

export const absoluteDate = (d: Date): string => {
  if (!d.setHours) {
    return format(new Date(new Date(d).setHours(1)), 'yyyy-MM-dd')
  }
  return format(new Date(d.setHours(1)), 'yyyy-MM-dd')
}

const lastSunday = (month: number, year: number) => {
  const d = new Date()
  const lastDayOfMonth = new Date(Date.UTC(year || d.getFullYear(), month + 1, 0))
  const day = lastDayOfMonth.getDay()
  return new Date(
    Date.UTC(
      lastDayOfMonth.getFullYear(),
      lastDayOfMonth.getMonth(),
      lastDayOfMonth.getDate() - day
    )
  )
}

const isBST = (date: Date) => {
  const d = date || new Date()
  const starts = lastSunday(2, d.getFullYear())
  starts.setHours(1)
  const ends = lastSunday(9, d.getFullYear())
  starts.setHours(1)
  return d.getTime() >= starts.getTime() && d.getTime() < ends.getTime()
}

export const formatDateWithTimeZone = (dateStr: Date | string) => {
  let formattedDate
  if (Object.prototype.toString.call(dateStr) === '[object Date]') {
    formattedDate = dateStr as Date
  } else {
    formattedDate = new Date(dateStr)
  }

  let userTimezoneOffset = formattedDate.getTimezoneOffset() * 60000
  if (isBST(formattedDate)) {
    userTimezoneOffset += 3600000
  }
  const finalDate =
    userTimezoneOffset >= 0
      ? new Date(formattedDate.getTime() + userTimezoneOffset)
      : new Date(formattedDate.getTime() - userTimezoneOffset)
  return finalDate
}

export const formatDateWithTimeZoneStr = (dateStr: Date | string, formatStr?: string) => {
  if (formatStr) {
    return format(formatDateWithTimeZone(dateStr), formatStr)
  }

  return format(formatDateWithTimeZone(dateStr), 'yyyy-MM-dd')
}

export const formatLocalDateYmd = (date: Date) => format(date, 'yyyy-MM-dd')

export const formatDateTimeWithTimeZoneBST = (dateStr: Date | string) => {
  let formattedDate
  if (Object.prototype.toString.call(dateStr) === '[object Date]') {
    formattedDate = dateStr as Date
  } else {
    formattedDate = new Date(dateStr)
  }

  let userTimezoneOffset = formattedDate.getTimezoneOffset() * 60000

  if (isBST(formattedDate)) {
    userTimezoneOffset += 3600000
  }

  return new Date(formattedDate.getTime() + userTimezoneOffset)
}

export const formatDatTimeWithTimeZone = (dateStr: Date | string) => {
  let formattedDate
  if (Object.prototype.toString.call(dateStr) === '[object Date]') {
    formattedDate = dateStr as Date
  } else {
    formattedDate = new Date(dateStr)
  }

  const userTimezoneOffset = formattedDate.getTimezoneOffset() * 60000

  return new Date(formattedDate.getTime() + userTimezoneOffset)
}

export const formatDatTimeWithTimeZoneStr = (dateStr: Date | string) =>
  format(formatDatTimeWithTimeZone(dateStr), 'yyyy-MM-dd HH:mm')

export const groupDatesIntoRanges = (dates: Date[]): [[Date, Date]?] => {
  if (!dates.length) {
    return []
  }
  const localDates: [[Date, Date]?] = []
  let firstDate = new Date()
  let lastDate = new Date()
  let cnt = -1
  dates.forEach((date, idx) => {
    const currentIterationDate = formatDateWithTimeZone(date)
    cnt += 1
    if (idx === 0) {
      firstDate = currentIterationDate
      lastDate = currentIterationDate
      return
    }
    if (
      format(addDays(firstDate, cnt), 'yyyy-MM-dd hh-mm-ss') <
      format(currentIterationDate, 'yyyy-MM-dd hh-mm-ss')
    ) {
      // The current interation date has advanced beyond the firstDate+increment,
      // indicating a contiguous break. e.g. 1st/2nd/3rd >>> 7th
      localDates.push([firstDate, lastDate])
      firstDate = currentIterationDate
      lastDate = currentIterationDate
      cnt = 0
      return
    }
    lastDate = currentIterationDate
  })
  localDates.push([firstDate, lastDate])
  return localDates
}

export const nextWorkingDay = (day: string | Date, areWeekendsWorked: boolean): Date => {
  const newDate = new Date(day)
  newDate.setHours(1)
  let increment = 1
  if (!areWeekendsWorked) {
    increment = newDate.getDay() === 5 ? 3 : increment
    increment = newDate.getDay() === 6 ? 2 : increment
  }
  return addDays(newDate, increment)
}

export const getOrdinal = (day: number): string => {
  if (day > 3 && day < 21) return `${day}th`
  switch (day % 10) {
    case 1:
      return `${day}st`
    case 2:
      return `${day}nd`
    case 3:
      return `${day}rd`
    default:
      return `${day}th`
  }
}

export const getMonday = (date: Date, previous: boolean, skipCurrent = false) => {
  let localDate = new Date(date)
  if (skipCurrent && localDate.getDay() === 1) {
    localDate = previous ? subDays(localDate, 1) : addDays(localDate, 1)
  }
  while (localDate.getDay() !== 1) {
    localDate = previous ? subDays(localDate, 1) : addDays(localDate, 1)
  }
  return localDate
}

export const getLongMonthString = (date: string | Date) =>
  new Date(date).toLocaleDateString(navigator.language, { month: 'long' })

export const formatDatesToDelimitedString = (dates: Date[]) =>
  dates.map(date => getLocalDateString(date)).join(', ')

export const formatSickPayTakenHours = (value: number | undefined): string =>
  value ? `${value.toFixed(2)} Hours` : 'No'

const padTwoDigits = (num: number) => num.toString().padStart(2, '0')

export const dateInYyyyMmDdHhMmSs = (date: Date, dateDiveder = '-') =>
  `${[date.getFullYear(), padTwoDigits(date.getMonth() + 1), padTwoDigits(date.getDate())].join(
    dateDiveder
  )} ${[
    padTwoDigits(date.getHours()),
    padTwoDigits(date.getMinutes()),
    padTwoDigits(date.getSeconds()),
  ].join(':')}`

export const dateTimeToNumber = (date: Date | string): number => {
  if (Object.prototype.toString.call(date) === '[object Date]') {
    return Number(format(date as Date, 'yyyyMMddHHmm'))
  }
  return Number(format(new Date(date), 'yyyyMMddHHmm'))
}



const timeFormatOptions: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
}

export const formatTime = (date?: Date | null, excludeSeconds = false): string =>
  date?.toLocaleTimeString(navigator.language, { ...timeFormatOptions,
    second: excludeSeconds ? undefined : timeFormatOptions.second }) ?? ''

export const getDurationInFractionalHours = (start: Date, end: Date): number => {
  const minutesDifference = differenceInMinutes(end, start)
  const hoursDiffInFractions = minutesDifference / 60
  return +hoursDiffInFractions.toFixed(2)
}

export const isWeekend = (date: Date): boolean => date.getDay() === 6 || date.getDay() === 0

export const formatDateTimeWithLocale = (dateString: string | Date): string =>
  new Date(dateString).toLocaleString(navigator.language, { dateStyle: 'short', timeStyle: 'short' })

export const formatDateTimeWithLocaleWithDay = (dateString: string | Date): string => {
  const date = new Date(dateString)
  const weekday = date.toLocaleDateString(navigator.language, { weekday: 'short' })
  const dateTime = date.toLocaleString(navigator.language, { dateStyle: 'short', timeStyle: 'short' })
  return `${weekday} ${dateTime}`
}

export const formatDateWithLocale = (dateString: string | Date): string =>
  new Date(dateString).toLocaleDateString(navigator.language)

/**
 * Creates a Date object from a time string (HH:mm or HH:mm:ss format)
 * The date part will be set to today's date.
 * Meant for instances where the date part is not important, e.g. time picker.
 * @param timeString Time string in format "HH:mm" or "HH:mm:ss"
 * @returns Date object with the specified time
 */
export function createDateFromTimeString(timeString: string): Date {
  if (!/^([0-1]?[0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/.test(timeString)) {
    throw new Error('Invalid time format. Expected "HH:mm" or "HH:mm:ss"')
  }
  
  const parseFormat = timeString.split(':').length === 3 ? 'HH:mm:ss' : 'HH:mm'
  
  return parse(timeString, parseFormat, new Date())
}

export const toMidnight = (date: Date) => {
  const midnightDate = new Date(date)
  midnightDate.setHours(0, 0, 0, 0)
  return midnightDate
}

export const currentDayMidnight = () => toMidnight(new Date())

/**
 * Determines if the given date is valid in the context of the planner365 app.
 * The main goal of this function is to ensure that API requests containing the given date will not result
 * in an error due to the date missing some segments, or the year being out of supported range
 * @param date 
 * @returns true if the date is valid in the context of the planner365 app, false otherwise.
 */
export const isValidPlannerDate = (date: Date | null): date is Date => date !== null && isValid(date) && date.getFullYear() >= 2000

export const isValidPlannerDateRange = (dateRange: DateRange<Date>): dateRange is NonEmptyDateRange<Date> =>
  isValidPlannerDate(dateRange[0]) && isValidPlannerDate(dateRange[1])

export const formatDateOrDefault = (
    date: Date | null | undefined,
    defaultDate: Date
  ): string => {
    if (date && date instanceof Date && !Number.isNaN(date.getTime())) {
      return format(date, 'yyyy-MM-dd')
    }
    return format(defaultDate, 'yyyy-MM-dd')
  }
